import { useCallback } from 'react';
import axios from 'axios';
import { toCommonPRDetails } from '../adapters/prAdapters';

// Shared fix workflow for PR details pages. Currently implements GitHub endpoints.
export default function useFixWorkflow({
  provider = 'github',
  API_BASE,
  identifiers, // { organization, project, repositoryID, prNumber }
  getProjectKey,
  normalizePath,
  refs: { fixControllersRef },
  state: {
    appliedFixes,
    setAppliedFixes,
    appliedTokens,
    setAppliedTokens,
    intentionalStaged,
    setIntentionalStaged,
    setError,
    setLoadingFileMap,
    setFileCache,
    setPrDetails,
  },
  ui: { setFixDialogOpen, setFixLoading, setFixedFile, setFixToken, setPendingFixMeta },
  storageKey,
}) {
  const { organization, project, repositoryID, prNumber } = identifiers || {};

  const base = (path) => {
    if (provider === 'github') {
      return `${API_BASE}/api/github/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}${path}`;
    }
    if (provider === 'bitbucket') {
      return `${API_BASE}/api/bitbucket/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}${path}`;
    }
    if (provider === 'azure') {
      return `${API_BASE}/api/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${prNumber}${path}`;
    }
    if (provider === 'gitlab') {
      // organization => projectID for GitLab in this hook's identifiers
      const projectID = organization;
      return `${API_BASE}/api/gitlab/projects/${encodeURIComponent(projectID)}/merge-requests/${prNumber}${path}`;
    }
    return `${API_BASE}/api/github/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}${path}`;
  };

  const handleFixRequest = useCallback(async (comment, line) => {
    setFixDialogOpen(true); setFixLoading(true); setFixedFile(null); setFixToken(null); setPendingFixMeta(null);
    const tidRaw = (comment && (comment.thread_id ?? comment.threadId ?? comment.ThreadID));
    const tidNum = typeof tidRaw === 'string' ? parseInt(tidRaw, 10) : tidRaw;
    setPendingFixMeta({ mode: 'comment', filePath: comment.file, commentThreads: Number.isFinite(tidNum) ? [tidNum] : [] });
    let controller;
    try {
      const projectKey = getProjectKey();
      if (!projectKey) throw new Error('Missing project key');
      controller = new AbortController();
      fixControllersRef.current.add(controller);
      const response = await axios.post(base('/fix'), {
        mode: 'comment',
        comment,
        line,
        file: comment.file,
        base_content: (appliedFixes && comment && comment.file && appliedFixes[comment.file]?.fixed) ? appliedFixes[comment.file].fixed : undefined,
        intentional_threads: Array.isArray(intentionalStaged?.[comment.file]) ? intentionalStaged[comment.file] : [],
      }, { headers: { 'X-API-KEY': projectKey }, signal: controller.signal });
      setFixedFile(response.data); setFixToken(response.data?.token || null);
    } catch (error) {
      const canceled = (error && (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || (typeof axios.isCancel === 'function' && axios.isCancel(error))));
      if (!canceled) {
        // For any fix generation failure, keep dialog open and show manual-fix message
        setFixedFile(null); setFixToken(null);
      }
    } finally { setFixLoading(false); try { if (controller) fixControllersRef.current.delete(controller); } catch {} }
  }, [API_BASE, provider, organization, project, prNumber, getProjectKey, fixControllersRef, appliedFixes, intentionalStaged, setFixDialogOpen, setFixLoading, setFixedFile, setFixToken, setPendingFixMeta, setError]);

  const handleMarkIntentional = useCallback((comment) => {
    const filePath = comment.file;
    const tidRaw = (comment && (comment.thread_id ?? comment.threadId ?? comment.ThreadID));
    const tid = typeof tidRaw === 'string' ? parseInt(tidRaw, 10) : tidRaw;
    if (!filePath || !Number.isFinite(tid)) return;
    setIntentionalStaged(prev => {
      const prevArr = Array.isArray(prev[filePath]) ? prev[filePath] : [];
      if (prevArr.includes(tid)) return prev;
      const next = { ...prev, [filePath]: [...prevArr, tid] };
      if (typeof storageKey === 'string') {
        try {
          const payload = JSON.parse(localStorage.getItem(storageKey) || '{}');
          const fixes = payload.appliedFixes || appliedFixes || {};
          const tokens = payload.appliedTokens || appliedTokens || [];
          try { localStorage.setItem(storageKey, JSON.stringify({ ...payload, appliedFixes: fixes, appliedTokens: tokens, intentionalStaged: next })); } catch {}
        } catch {}
      }
      return next;
    });
  }, [setIntentionalStaged, storageKey, appliedFixes, appliedTokens]);

  const handleFixFileRequest = useCallback(async (file) => {
    const fileKey = file.path || file.filename;
    setFixDialogOpen(true); setFixLoading(true); setFixedFile(null); setFixToken(null); setPendingFixMeta(null);
    const commentThreads = (file.comments || []).map(c => (typeof (c.thread_id ?? c.threadId ?? c.ThreadID) === 'string' ? parseInt(c.thread_id ?? c.threadId ?? c.ThreadID, 10) : (c.thread_id ?? c.threadId ?? c.ThreadID))).filter(v => Number.isFinite(v));
    setPendingFixMeta({ mode: 'file', filePath: fileKey, commentThreads });
    let controller;
    try {
      const projectKey = getProjectKey();
      if (!projectKey) throw new Error('Missing project key');
      controller = new AbortController();
      fixControllersRef.current.add(controller);
      const response = await axios.post(base('/fix'), {
        mode: 'file',
        comments: file.comments || [],
        file: fileKey,
        diff: file.diff_lines || [],
        base_content: (appliedFixes && fileKey && appliedFixes[fileKey]?.fixed) ? appliedFixes[fileKey].fixed : undefined,
        intentional_threads: Array.isArray(intentionalStaged?.[fileKey]) ? intentionalStaged[fileKey] : [],
      }, { headers: { 'X-API-KEY': projectKey }, signal: controller.signal });
      setFixedFile(response.data); setFixToken(response.data?.token || null);
    } catch (error) {
      const canceled = (error && (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || (typeof axios.isCancel === 'function' && axios.isCancel(error))));
      if (!canceled) {
        // For any file fix generation failure, keep dialog open and show manual-fix message
        setFixedFile(null); setFixToken(null);
      }
    } finally { setFixLoading(false); }
  }, [API_BASE, provider, organization, project, prNumber, getProjectKey, fixControllersRef, appliedFixes, intentionalStaged, setFixDialogOpen, setFixLoading, setFixedFile, setFixToken, setPendingFixMeta, setError]);

  const handleCommitFix = useCallback(async ({ fixedFile, fixToken, pendingFixMeta }) => {
    if (!fixedFile || !fixToken) return;
    const filePath = fixedFile.file_path;
    setAppliedFixes(prev => {
      const prevThreads = prev[filePath]?.commentThreadsApplied || [];
      const incomingThreads = ((pendingFixMeta && pendingFixMeta.filePath === filePath) ? (pendingFixMeta.commentThreads || []) : []);
      const mergedThreads = Array.from(new Set([
        ...prevThreads.filter(v => Number.isFinite(typeof v === 'string' ? parseInt(v, 10) : v)).map(v => (typeof v === 'string' ? parseInt(v, 10) : v)),
        ...incomingThreads.filter(v => Number.isFinite(typeof v === 'string' ? parseInt(v, 10) : v)).map(v => (typeof v === 'string' ? parseInt(v, 10) : v)),
      ]));
      const intentSet = new Set((intentionalStaged?.[filePath] || []).map(v => (typeof v === 'string' ? parseInt(v, 10) : v)).filter(Number.isFinite));
      const mergedFiltered = mergedThreads.filter(t => !intentSet.has(t));
      const prevOriginal = prev[filePath]?.original ?? fixedFile.original;
      const prevTokensForFile = Array.isArray(prev[filePath]?.tokens) ? prev[filePath].tokens : (prev[filePath]?.token ? [prev[filePath].token] : []);
      const nextTokensForFile = Array.from(new Set([...prevTokensForFile, fixToken]));
      const next = { ...prev, [filePath]: { token: fixToken, tokens: nextTokensForFile, original: prevOriginal, fixed: fixedFile.content, commentThreadsApplied: mergedFiltered } };
      if (typeof storageKey === 'string') {
        try {
          const payload = JSON.parse(localStorage.getItem(storageKey) || '{}');
          const tokens = Array.from(new Set([...(payload.appliedTokens || []), fixToken]));
          localStorage.setItem(storageKey, JSON.stringify({ ...payload, appliedFixes: next, appliedTokens: tokens, intentionalStaged: intentionalStaged || {} }));
        } catch {}
      }
      return next;
    });
    setAppliedTokens(prev => Array.from(new Set([...(prev || []), fixToken])));
    setFixDialogOpen(false); setFixedFile(null); setFixToken(null); setPendingFixMeta(null);
    if (provider === 'azure') { return; }
    try {
      const projectKey = getProjectKey();
      if (projectKey && filePath) {
        setLoadingFileMap(prev => ({ ...prev, [filePath]: true }));
        const { data } = await axios.get(base('/file'), { params: { path: filePath }, headers: { 'X-API-KEY': projectKey } });
        setFileCache(prev => ({ ...prev, [filePath]: { diff_lines: data?.diff_lines || [], comments: data?.comments || [] } }));
        setPrDetails(prev => {
          if (!prev) return prev;
          const files = Array.isArray(prev.files) ? [...prev.files] : [];
          const idx = files.findIndex(f => normalizePath(f.path || f.filename || '') === normalizePath(filePath));
          if (idx >= 0) {
            const curr = files[idx] || {};
            files[idx] = { ...curr, diff_lines: data?.diff_lines || [], comments: data?.comments || [] };
          }
          const existingPRLevel = Array.isArray(prev.comments) ? prev.comments.filter(c => !c.file) : [];
          const allFileComments = files.flatMap(f => Array.isArray(f.comments) ? f.comments : []);
          const combined = [...existingPRLevel, ...allFileComments];
          return { ...prev, files, comments: combined, total_comments: combined.length };
        });
      }
    } catch {}
    finally {
      setLoadingFileMap(prev => { const next = { ...prev }; delete next[filePath]; return next; });
    }
  }, [provider, getProjectKey, setAppliedFixes, setAppliedTokens, setFixDialogOpen, setFixedFile, setFixToken, setPendingFixMeta, setLoadingFileMap, setFileCache, setPrDetails, intentionalStaged, storageKey, normalizePath, setFixLoading]);

  const handleDiscardFix = useCallback(async ({ fixToken, fixedFile }) => {
    if (!fixToken) { setFixDialogOpen(false); setFixedFile(null); return; }
    try {
      const projectKey = getProjectKey();
      if (!projectKey) throw new Error('Missing project key');
      await axios.delete(base(`/fix/${encodeURIComponent(fixToken)}`), { headers: { 'X-API-KEY': projectKey } });
      const filePath = fixedFile?.file_path;
      if (filePath) {
        try {
          await axios.delete(base('/cache'), { params: { path: filePath }, headers: { 'X-API-KEY': projectKey } });
        } catch {}
      }
    } catch {}
    finally { setFixDialogOpen(false); setFixedFile(null); setFixToken(null); }
  }, [getProjectKey, setFixDialogOpen, setFixedFile, setFixToken]);

  const handleCommitAll = useCallback(async () => {
    const stagedIntentionalCount = Object.values(intentionalStaged || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    if ((!appliedTokens || appliedTokens.length === 0) && stagedIntentionalCount === 0) return;
    try {
      const projectKey = getProjectKey();
      if (!projectKey) throw new Error('Missing project key');
      const tokensFromFiles = Object.values(appliedFixes || {}).flatMap((v) => { const entry = v || {}; const arr = Array.isArray(entry.tokens) ? entry.tokens : (entry.token ? [entry.token] : []); return arr; });
      const allTokens = Array.from(new Set([...(appliedTokens || []), ...tokensFromFiles]));
      const intentionalMarks = [];
      Object.entries(intentionalStaged || {}).forEach(([filePath, list]) => { (Array.isArray(list) ? list : []).forEach((tid) => { if (Number.isFinite(typeof tid === 'string' ? parseInt(tid, 10) : tid)) { intentionalMarks.push({ file: filePath, thread_id: (typeof tid === 'string' ? parseInt(tid, 10) : tid) }); } }); });
      await axios.post(base('/commit-fixes'), { tokens: allTokens, intentional_marks: intentionalMarks }, { headers: { 'X-API-KEY': projectKey } });
      const affected = new Set();
      Object.keys(appliedFixes || {}).forEach(fp => { if (fp) affected.add(fp); });
      Object.keys(intentionalStaged || {}).forEach(fp => { if (fp) affected.add(fp); });
      const affectedPaths = Array.from(affected);
      if (provider === 'azure') {
        const { data } = await axios.get(base('/details'), { headers: { 'X-API-KEY': projectKey } });
        const normalized = toCommonPRDetails('azure', data || {});
        const filesIncoming = Array.isArray(normalized.files) ? normalized.files : [];
        const byPath = new Map(filesIncoming.map(f => [normalizePath(f.path || f.filename || ''), f]));
        setPrDetails(prev => {
          if (!prev) return prev;
          const filesPrev = Array.isArray(prev.files) ? [...prev.files] : [];
          const nextFiles = filesPrev.map(f => {
            const key = normalizePath(f.path || f.filename || '');
            return affectedPaths.some(p => normalizePath(p) === key) && byPath.has(key)
              ? { ...f, ...byPath.get(key) }
              : f;
          });
          affectedPaths.forEach(p => {
            const k = normalizePath(p);
            const incoming = byPath.get(k);
            if (incoming) { setFileCache(prevCache => ({ ...prevCache, [k]: { diff_lines: incoming.diff_lines || [], comments: incoming.comments || [] } })); }
          });
          const existingPRLevel = Array.isArray(prev.comments) ? prev.comments.filter(c => !c.file) : [];
          const allFileComments = nextFiles.flatMap(f => Array.isArray(f.comments) ? f.comments : []);
          const combined = [...existingPRLevel, ...allFileComments];
          return { ...prev, files: nextFiles, comments: combined, total_comments: combined.length };
        });
      } else if (affectedPaths.length > 0) {
        await Promise.allSettled(affectedPaths.map(async (fp) => {
          try {
            setLoadingFileMap(prev => ({ ...prev, [fp]: true }));
            const { data } = await axios.get(base('/file'), { params: { path: fp }, headers: { 'X-API-KEY': projectKey } });
            setFileCache(prev => ({ ...prev, [fp]: { diff_lines: data?.diff_lines || [], comments: data?.comments || [] } }));
            setPrDetails(prev => {
              if (!prev) return prev;
              const files = Array.isArray(prev.files) ? [...prev.files] : [];
              const idx = files.findIndex(f => normalizePath(f.path || f.filename || '') === normalizePath(fp));
              if (idx >= 0) {
                const curr = files[idx] || {};
                files[idx] = { ...curr, diff_lines: data?.diff_lines || [], comments: data?.comments || [] };
              }
              const existingPRLevel = Array.isArray(prev.comments) ? prev.comments.filter(c => !c.file) : [];
              const allFileComments = files.flatMap(f => Array.isArray(f.comments) ? f.comments : []);
              const combined = [...existingPRLevel, ...allFileComments];
              return { ...prev, files, comments: combined, total_comments: combined.length };
            });
          } finally {
            setLoadingFileMap(prev => { const next = { ...prev }; delete next[fp]; return next; });
          }
        }));
      }
      setAppliedFixes({}); setAppliedTokens([]); setIntentionalStaged({}); try { localStorage.removeItem(storageKey); } catch {}
    } catch (error) {
      setError && setError(`Failed to commit fixes: ${error?.response?.data?.error || error?.message}`);
    }
  }, [provider, getProjectKey, appliedTokens, appliedFixes, intentionalStaged, setAppliedFixes, setAppliedTokens, setIntentionalStaged, setLoadingFileMap, setFileCache, setPrDetails, storageKey, setError, normalizePath]);

  const handleDiscardAll = useCallback(async () => {
    try {
      const projectKey = getProjectKey();
      if (!projectKey) throw new Error('Missing project key');
      for (const t of appliedTokens || []) {
        try { await axios.delete(base(`/fix/${encodeURIComponent(t)}`), { headers: { 'X-API-KEY': projectKey } }); } catch {}
      }
      try { await axios.delete(base('/cache'), { headers: { 'X-API-KEY': projectKey } }); } catch {}
    } finally {
      setAppliedFixes({}); setAppliedTokens([]); setIntentionalStaged({}); try { localStorage.removeItem(storageKey); } catch {}
    }
  }, [getProjectKey, appliedTokens, setAppliedFixes, setAppliedTokens, setIntentionalStaged, storageKey]);

  const handleDiscardApplied = useCallback(async (filePath) => {
    if (!filePath) return;
    const stagedEntry = (appliedFixes && appliedFixes[filePath]) || {};
    const tokensForFile = Array.isArray(stagedEntry.tokens) ? stagedEntry.tokens : (stagedEntry.token ? [stagedEntry.token] : []);
    try {
      const projectKey = getProjectKey();
      if (!projectKey) throw new Error('Missing project key');
      for (const t of tokensForFile) {
        try { await axios.delete(base(`/fix/${encodeURIComponent(t)}`), { headers: { 'X-API-KEY': projectKey } }); } catch {}
      }
      try { await axios.delete(base('/cache'), { params: { path: filePath }, headers: { 'X-API-KEY': projectKey } }); } catch {}
    } finally {
      const nextTokensGlobal = (appliedTokens || []).filter(tok => !tokensForFile.includes(tok));
      const nextIntentional = { ...(intentionalStaged || {}) };
      delete nextIntentional[filePath];
      setAppliedFixes(prev => {
        const next = { ...prev };
        delete next[filePath];
        if (typeof storageKey === 'string') {
          try {
            const payload = JSON.parse(localStorage.getItem(storageKey) || '{}');
            localStorage.setItem(storageKey, JSON.stringify({ ...payload, appliedFixes: next, appliedTokens: nextTokensGlobal, intentionalStaged: nextIntentional }));
          } catch {}
        }
        return next;
      });
      setAppliedTokens(nextTokensGlobal);
      setIntentionalStaged(nextIntentional);
    }
  }, [appliedFixes, appliedTokens, intentionalStaged, getProjectKey, setAppliedFixes, setAppliedTokens, setIntentionalStaged, storageKey]);

  const handleViewAppliedFix = useCallback((filePath) => {
    const staged = appliedFixes && appliedFixes[filePath];
    if (!staged) return;
    setFixedFile({ file_path: filePath, original: staged.original, content: staged.fixed });
    setFixDialogOpen(true); setFixLoading(false);
  }, [appliedFixes, setFixedFile, setFixDialogOpen, setFixLoading]);

  return {
    handleFixRequest,
    handleMarkIntentional,
    handleFixFileRequest,
    handleCommitFix,
    handleDiscardFix,
    handleCommitAll,
    handleDiscardAll,
    handleDiscardApplied,
    handleViewAppliedFix,
    handleResolveComment: async (comment) => {
      try {
        if (provider !== 'azure') return; 
        const projectKey = getProjectKey();
        if (!projectKey) throw new Error('Missing project key');
        const tidRaw = (comment && (comment.thread_id ?? comment.threadId ?? comment.ThreadID));
        const threadId = typeof tidRaw === 'string' ? parseInt(tidRaw, 10) : tidRaw;
        const filePath = comment?.file;
        if (!Number.isFinite(threadId) || !filePath) return;
        await axios.post(base('/commit-fixes'), { tokens: [], intentional_marks: [{ file: filePath, thread_id: threadId }] }, { headers: { 'X-API-KEY': projectKey } });
        // Refresh the single file
        try {
          setLoadingFileMap(prev => ({ ...prev, [filePath]: true }));
          const { data } = await axios.get(base('/file'), { params: { path: filePath }, headers: { 'X-API-KEY': projectKey } });
          setFileCache(prev => ({ ...prev, [filePath]: { diff_lines: data?.diff_lines || [], comments: data?.comments || [] } }));
          setPrDetails(prev => {
            if (!prev) return prev;
            const files = Array.isArray(prev.files) ? [...prev.files] : [];
            const idx = files.findIndex(f => normalizePath(f.path || f.filename || '') === normalizePath(filePath));
            if (idx >= 0) {
              const curr = files[idx] || {};
              files[idx] = { ...curr, diff_lines: data?.diff_lines || [], comments: data?.comments || [] };
            }
            const existingPRLevel = Array.isArray(prev.comments) ? prev.comments.filter(c => !c.file) : [];
            const allFileComments = files.flatMap(f => Array.isArray(f.comments) ? f.comments : []);
            const combined = [...existingPRLevel, ...allFileComments];
            return { ...prev, files, comments: combined, total_comments: combined.length };
          });
        } finally {
          setLoadingFileMap(prev => { const next = { ...prev }; delete next[filePath]; return next; });
        }
      } catch (e) {
        setError && setError(`Failed to resolve comment: ${e?.response?.data?.error || e?.message}`);
      }
    },
  };
}


